import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './VeranstalterLandingPage.css';
import PublicNav from '../components/PublicNav';
import FunktionsBanner from '../components/FunktionsBanner';

const VORTEILE = [
  { icon: '🏆', titel: 'Professionelles Turniermanagement', text: 'Kategorien, Brackets, Zeitpläne, Ergebnisse — alles in einem System. Kein Excel, kein Chaos.' },
  { icon: '📋', titel: 'Online-Anmeldung für Teilnehmer', text: 'Vereine melden ihre Kämpfer direkt online an. Du siehst alles live, ohne manuelle Listen.' },
  { icon: '⚖️', titel: 'Integriertes Einwaagesystem', text: 'Digitale Einwaage mit automatischer Einteilung in Gewichtsklassen — schnell, fehlerfrei.' },
  { icon: '📺', titel: 'Live-Streaming & Anzeige', text: 'Ergebnisse live auf dem zweiten Bildschirm, Zuschauer-Modus, Siegerehrung-Display.' },
  { icon: '🥊', titel: 'Automatische Bracket-Erstellung', text: 'KO, Doppel-KO, Round-Robin — Brackets werden in Sekunden generiert und verwaltet.' },
  { icon: '📊', titel: 'Berichte & Statistiken', text: 'Teilnehmerlisten, Finanzbericht, Ergebnisübersicht — alles als PDF oder Export.' },
  { icon: '🏅', titel: 'Ranglisten & Saison-Wertung', text: 'Automatische Punktevergabe für Saison-Ranglisten über mehrere Turniere hinweg.' },
  { icon: '🔒', titel: 'DSGVO-konform & sicher', text: 'Deutsche Server, SSL-verschlüsselt, datenschutzkonform. Alle Daten bleiben bei dir.' },
];

const SCHRITTE = [
  { nr: '01', titel: 'Registrieren', text: 'Erstelle deinen kostenlosen Veranstalter-Account in unter 2 Minuten.' },
  { nr: '02', titel: 'Turnier anlegen', text: 'Trage Datum, Ort, Disziplinen und Kategorien ein. Das System erledigt den Rest.' },
  { nr: '03', titel: 'Anmeldungen öffnen', text: 'Vereine erhalten den Link und melden ihre Kämpfer direkt online an.' },
  { nr: '04', titel: 'Turnier durchführen', text: 'Live-Brackets, Schreiber-Panels, Ergebnisse in Echtzeit — alles läuft digital.' },
];

const PAKETE = [
  {
    name: 'Einzelturnier',
    preis: 'auf Anfrage',
    preisHint: 'pro Veranstaltung',
    highlight: false,
    features: [
      'Ein Turnier / Event',
      'Unbegrenzte Teilnehmer',
      'Online-Anmeldung',
      'Bracket-Management',
      'Ergebnisse & Berichte',
      'E-Mail-Support',
    ],
  },
  {
    name: 'Saison-Paket',
    preis: 'auf Anfrage',
    preisHint: 'pro Saison / Jahr',
    highlight: true,
    badge: 'Beliebt',
    features: [
      'Unbegrenzte Turniere',
      'Saison-Rangliste',
      'Live-Streaming-Modul',
      'Einwaage-System',
      'Prioritäts-Support',
      'Individuelle Konfiguration',
    ],
  },
  {
    name: 'Verband',
    preis: 'auf Anfrage',
    preisHint: 'individuelle Lizenz',
    highlight: false,
    features: [
      'Multi-Turnier-Verwaltung',
      'Verbandsrangliste',
      'Vereins-Verwaltung',
      'White-Label-Option',
      'API-Zugang',
      'Dedizierter Support',
    ],
  },
];

const FAQS = [
  { frage: 'Brauche ich technisches Know-how?', antwort: 'Nein. Das System ist so aufgebaut, dass du ohne Vorkenntnisse sofort starten kannst. Bei Fragen helfen wir dir persönlich.' },
  { frage: 'Kann ich das System kostenlos testen?', antwort: 'Ja. Wir bieten dir einen Demozugang an, mit dem du das System unverbindlich kennenlernen kannst.' },
  { frage: 'Was passiert mit den Daten der Teilnehmer?', antwort: 'Alle Daten werden auf deutschen Servern gespeichert und sind vollständig DSGVO-konform. Du hast jederzeit die volle Kontrolle.' },
  { frage: 'Welche Kampfsport-Disziplinen werden unterstützt?', antwort: 'Taekwondo, Karate, Judo, Kickboxen, Grappling, Formen, Selbstverteidigung, Bruchtest und weitere. Neue Disziplinen können ergänzt werden.' },
  { frage: 'Wie läuft die Abrechnung ab?', antwort: 'Du zahlst entweder pro Turnier oder buchst ein Saison-Paket. Beide Modelle stehen zur Wahl. Die genauen Konditionen besprechen wir individuell.' },
  { frage: 'Gibt es Schulungen oder Einführungen?', antwort: 'Ja. Für Neukunden bieten wir eine persönliche Einführung per Video-Call an. Zudem gibt es eine Hilfedokumentation direkt im System.' },
];

function FAQ({ frage, antwort }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`vl-faq-item${open ? ' vl-faq-item--open' : ''}`} onClick={() => setOpen(o => !o)}>
      <div className="vl-faq-q">
        <span>{frage}</span>
        <span className="vl-faq-icon">{open ? '−' : '+'}</span>
      </div>
      {open && <div className="vl-faq-a">{antwort}</div>}
    </div>
  );
}

export default function VeranstalterLandingPage() {
  const [form, setForm] = useState({ name: '', organisation: '', email: '', telefon: '', paket: '', nachricht: '' });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    setSending(true);
    setError('');
    try {
      const res = await fetch('/api/veranstalter/anfrage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError('Fehler beim Senden. Bitte versuche es erneut oder schreibe uns direkt.');
      }
    } catch {
      setError('Verbindungsfehler. Bitte versuche es später erneut.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="vl-page">
      <PublicNav />

      {/* HERO-BANNER (Software-Bild) */}
      {/* Vorläufig Text statt /platform-features-v2.webp (verstümmelter Text im
          KI-Bild); ein neues Bild kommt später, die Datei bleibt in public/. */}
      <FunktionsBanner />

      {/* HERO */}
      <section className="vl-hero">
        <div className="vl-hero-inner">
          <div className="vl-hero-eyebrow">Für Turnierveranstalter</div>
          <h1 className="vl-hero-title">
            Dein Turnier.<br />
            <span className="vl-hero-gold">Professionell organisiert.</span>
          </h1>
          <p className="vl-hero-sub">
            Die Software, mit der führende Kampfsport-Verbände und Vereine ihre Turniere
            digitalisieren. Von der Anmeldung bis zur Siegerehrung — alles in einem System.
          </p>
          <div className="vl-hero-cta">
            <a href="#registrierung" className="vl-btn-primary">Jetzt Zugang anfragen</a>
            <a href="#so-funktionierts" className="vl-btn-ghost">Wie funktioniert's?</a>
          </div>
          <div className="vl-hero-badges">
            <span>🔒 DSGVO-konform</span>
            <span>🇩🇪 Deutsche Server</span>
            <span>⚡ Sofort einsatzbereit</span>
          </div>
        </div>
      </section>

      {/* VORTEILE */}
      <section className="vl-section vl-vorteile-section">
        <div className="vl-container">
          <h2 className="vl-section-title">Alles, was du als Veranstalter brauchst</h2>
          <p className="vl-section-sub">Kein Zusammensuchen von Tools. Alles an einem Ort.</p>
          <div className="vl-vorteile-grid">
            {VORTEILE.map((v, i) => (
              <div key={i} className="vl-vorteil-card">
                <div className="vl-vorteil-icon">{v.icon}</div>
                <h3>{v.titel}</h3>
                <p>{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SO FUNKTIONIERT'S */}
      <section className="vl-section vl-steps-section" id="so-funktionierts">
        <div className="vl-container">
          <h2 className="vl-section-title">In 4 Schritten zum professionellen Turnier</h2>
          <div className="vl-steps">
            {SCHRITTE.map((s, i) => (
              <div key={i} className="vl-step">
                <div className="vl-step-nr">{s.nr}</div>
                <div className="vl-step-content">
                  <h3>{s.titel}</h3>
                  <p>{s.text}</p>
                </div>
                {i < SCHRITTE.length - 1 && <div className="vl-step-arrow">→</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PAKETE */}
      <section className="vl-section vl-pakete-section" id="preise">
        <div className="vl-container">
          <h2 className="vl-section-title">Flexible Modelle</h2>
          <p className="vl-section-sub">Ob einmaliges Turnier oder ganze Saison — du wählst, was zu dir passt.</p>
          <div className="vl-pakete-grid">
            {PAKETE.map((p, i) => (
              <div key={i} className={`vl-paket-card${p.highlight ? ' vl-paket-card--highlight' : ''}`}>
                {p.badge && <div className="vl-paket-badge">{p.badge}</div>}
                <h3 className="vl-paket-name">{p.name}</h3>
                <div className="vl-paket-preis">{p.preis}</div>
                <div className="vl-paket-hint">{p.preisHint}</div>
                <ul className="vl-paket-features">
                  {p.features.map((f, j) => (
                    <li key={j}><span className="vl-check">✓</span> {f}</li>
                  ))}
                </ul>
                <a href="#registrierung" className={p.highlight ? 'vl-btn-primary' : 'vl-btn-outline'}>
                  Anfragen
                </a>
              </div>
            ))}
          </div>
          <p className="vl-pakete-note">Alle Preise auf Anfrage. Wir erstellen dir ein individuelles Angebot.</p>
        </div>
      </section>

      {/* REGISTRIERUNGS-FORMULAR */}
      <section className="vl-section vl-reg-section" id="registrierung">
        <div className="vl-container vl-container--narrow">
          <h2 className="vl-section-title">Zugang anfragen</h2>
          <p className="vl-section-sub">Trag dich ein — wir melden uns innerhalb von 24 Stunden bei dir.</p>

          {submitted ? (
            <div className="vl-success-box">
              <div className="vl-success-icon">✓</div>
              <h3>Anfrage erhalten!</h3>
              <p>Wir haben deine Anfrage erhalten und melden uns in Kürze bei dir. Vielen Dank!</p>
            </div>
          ) : (
            <form className="vl-form" onSubmit={handleSubmit}>
              <div className="vl-form-row">
                <div className="vl-form-group">
                  <label>Name *</label>
                  <input name="name" value={form.name} onChange={handleChange} required placeholder="Dein Name" />
                </div>
                <div className="vl-form-group">
                  <label>Organisation / Verein / Verband</label>
                  <input name="organisation" value={form.organisation} onChange={handleChange} placeholder="z.B. Taekwondo Verband Bayern" />
                </div>
              </div>
              <div className="vl-form-row">
                <div className="vl-form-group">
                  <label>E-Mail *</label>
                  <input name="email" type="email" value={form.email} onChange={handleChange} required placeholder="deine@email.de" />
                </div>
                <div className="vl-form-group">
                  <label>Telefon</label>
                  <input name="telefon" value={form.telefon} onChange={handleChange} placeholder="+49 ..." />
                </div>
              </div>
              <div className="vl-form-group">
                <label>Paket-Interesse</label>
                <select name="paket" value={form.paket} onChange={handleChange}>
                  <option value="">Bitte wählen...</option>
                  <option value="einzelturnier">Einzelturnier</option>
                  <option value="saison">Saison-Paket</option>
                  <option value="verband">Verband</option>
                  <option value="noch-unklar">Noch unklar — ich möchte beraten werden</option>
                </select>
              </div>
              <div className="vl-form-group">
                <label>Nachricht / Fragen</label>
                <textarea name="nachricht" value={form.nachricht} onChange={handleChange} rows={4} placeholder="z.B. Wir planen ein Turnier mit ca. 200 Teilnehmern im September..." />
              </div>
              {error && <div className="vl-form-error">{error}</div>}
              <button type="submit" className="vl-btn-primary vl-btn-full" disabled={sending}>
                {sending ? 'Wird gesendet...' : 'Anfrage absenden →'}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="vl-section vl-faq-section">
        <div className="vl-container vl-container--narrow">
          <h2 className="vl-section-title">Häufige Fragen</h2>
          {FAQS.map((f, i) => <FAQ key={i} frage={f.frage} antwort={f.antwort} />)}
        </div>
      </section>

      {/* FOOTER CTA */}
      <section className="vl-footer-cta">
        <div className="vl-container">
          <h2>Bereit für dein nächstes Turnier?</h2>
          <p>Werde Teil der Veranstalter, die ihre Events bereits digital abwickeln.</p>
          <a href="#registrierung" className="vl-btn-primary">Jetzt Zugang anfragen</a>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="vl-footer">
        <div className="vl-container">
          <div className="vl-footer-links">
            <Link to="/">Zurück zur Startseite</Link>
            <a href="https://tda-intl.org/datenschutz">Datenschutz</a>
            <a href="https://tda-intl.org/impressum">Impressum</a>
            <Link to="/kontakt">Kontakt</Link>
          </div>
          <p className="vl-footer-copy">© {new Date().getFullYear()} TDA International. Alle Rechte vorbehalten.</p>
        </div>
      </footer>
    </div>
  );
}
